import React, { useEffect, useState, useRef } from 'react';
import axios from '../../utils/axiosInstance';
import { Link } from 'react-router-dom';
import { FaEdit, FaTrash, FaFileImport, FaFileExport } from 'react-icons/fa';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { hasPermission } from '../../utils/permissionHelper';
import Select from 'react-select';

const FamilyIndex = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const importInputRef = useRef();
  const tableContainerRef = useRef(); // Add this ref

  const user = JSON.parse(localStorage.getItem("user")); // logged in user

  const getInitialLimit = () => {
    const stored = localStorage.getItem('family_page_limit');
    return stored ? parseInt(stored) : 10;
  };
  const [limit, setLimit] = useState(getInitialLimit());
  const [plans, setPlans] = useState([]); // <-- Add plans state

  // Date filter states
  const [dateFilterType, setDateFilterType] = useState([]); // e.g. ['joiningDate', 'expirationDate']
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    localStorage.setItem('family_page_limit', limit);
  }, [limit]);

  useEffect(() => {
    if (hasPermission(user, "Family", "Read")) {
      fetchUsers(currentPage, searchQuery);
    }
    // eslint-disable-next-line
  }, [currentPage, searchQuery, limit, dateFilterType, dateRange.start, dateRange.end]);

  const fetchUsers = async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({
        userType: 'Family',
        page,
        limit,
        search,
      });
      // Add date filter params if set
      if (dateRange.start && dateRange.end && dateFilterType.length > 0) {
        // Only use the first checked type for filtering
        params.append('dateField', dateFilterType[0]);
        params.append('startDate', dateRange.start);
        params.append('endDate', dateRange.end);
      }
      const res = await axios.get(`/api/user?${params.toString()}`);
      const { users = [], totalCount = 0 } = res.data;
      setUsers(users);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      toastError('Failed to fetch users');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Family?')) return;
    try {
      await axios.delete(`/api/user/${id}?userType=Family`);
      toastSuccess('User deleted successfully!');
      setCurrentPage(1);
      fetchUsers(1, searchQuery);
    } catch (err) {
      toastError('Failed to delete user');
    }
  };

  const handleExport = async () => {
    try {
      const params = {
        search: searchQuery,
        userType: 'Family',
        page: currentPage,
        limit,
      };
      const response = await axios.get('/api/user/export-excel', {
        params,
        responseType: 'blob',
      });
      const disposition = response.headers['content-disposition'];
      let filename = 'familys.xlsx';
      if (disposition && disposition.includes('filename=')) {
        filename = disposition.split('filename=')[1].replace(/"/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toastSuccess('Export started');
    } catch (err) {
      toastError('Failed to export data');
    }
  };

  const handleOpenImportModal = () => {
    setImportModalVisible(true);
    setImportFile(null);
  };
  const handleCloseImportModal = () => {
    setImportModalVisible(false);
    setImportFile(null);
  };
  const handleImportFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) {
      toastError('Please select a file');
      return;
    }
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('userType', 'Family');
    try {
      const res = await axios.post(`/api/user/import-excel?userType=Family`, formData);
      if (res.data.Status) {
       //If res.data.failedCount contains the Excel path
        if (res.data.exportExcelUrl && res.data.failedCount > 0) {
          console.log("hnijgn");
          // Ensure no leading slash issues
          const failedFileUrl = process.env.REACT_APP_API_URL + res.data.exportExcelUrl; // handles with/without leading slash
          const link = document.createElement('a');
          link.href = failedFileUrl;
          link.setAttribute('download', 'failed_rows.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        toastError('Some rows failed to import.');
      } else {
        toastSuccess('File imported successfully!');
      }
      handleCloseImportModal();
      fetchUsers(currentPage, searchQuery, filters);
    } catch (err) {
      toastError('Import failed');
    }
  };
  const handleDownloadSample = () => {
    window.open(process.env.REACT_APP_DEMO_IMPORT_EXCEL_URL, '');
  };

  // Add Family Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormValues, setCreateFormValues] = useState({
    firstName: '',
    lastName: '',
    middleName: '',
    mobileNumber: '',
    email: '',
    business_name: '',
    business_category: '',
    business_sub_category: [],
    joiningDate: new Date().toISOString().split('T')[0],
    userType: 'Family',
    fees: '',
    plan: '', // <-- Add plan field
    source: '',           // <-- Add this
    invitedByUser: '',    // <-- Add this
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [businessDropdowns, setBusinessDropdowns] = useState({
    businessCategories: [],
    businessSubCategories: [],
  });
 
  // Add filters state (for consistency and to avoid no-undef)
  const [filters, setFilters] = useState({
    // Add any filter fields you need, or leave empty if not used
  });

  // Fetch business categories/subcategories for modal
  useEffect(() => {
    if (!showCreateModal) return;
    const fetchBusinessDropdowns = async () => {
      try {
        const [catRes, subCatRes] = await Promise.all([
          axios.get('/admin/api/businessCategory'),
          axios.get('/admin/api/businessSubCategory'),
        ]);
        setBusinessDropdowns({
          businessCategories: catRes.data?.businessCategories?.map(c => ({
            value: c._id,
            label: c.name,
          })) || [],
          businessSubCategories: subCatRes.data?.businessSubCategories?.map(sc => ({
            value: sc._id,
            label: sc.name,
            category: sc.category,
          })) || [],
        });
      } catch (err) {
        setBusinessDropdowns({ businessCategories: [], businessSubCategories: [] });
      }
    };
    fetchBusinessDropdowns();
  }, [showCreateModal]);

  // Fetch plans only when modal opens
  useEffect(() => {
    if (!showCreateModal) return;
    const fetchPlans = async () => {
      try {
        // Fetch only Family plans
        const res = await axios.get('/api/plan?userType=Family');
        const planOptions = res.data.plans?.map(p => ({
          value: p._id,
          label: p.name
        })) || [];
        setPlans(planOptions);
        // Set the first plan as selected in form state
        if (planOptions.length > 0) {
          setCreateFormValues(v => ({
            ...v,
            plan: planOptions[0].value,
            planName: planOptions[0].label // add planName for display
          }));
        } else {
          setCreateFormValues(v => ({
            ...v,
            plan: '',
            planName: ''
          }));
        }
      } catch (err) {
        setPlans([]);
        setCreateFormValues(v => ({
          ...v,
          plan: '',
          planName: ''
        }));
      }
    };
    fetchPlans();
  }, [showCreateModal]);

  // Fetch users for "Invited User" dropdown (members only)
  const [memberUsers, setMemberUsers] = useState([]);
  useEffect(() => {
    if (!showCreateModal) return;
    const fetchMembers = async () => {
      try {
        const params = new URLSearchParams({
          userType: 'Member',
          page: 1,
          limit: 1000, // get all for dropdown
        });
        const res = await axios.get(`/api/user?${params.toString()}`);
        setMemberUsers(res.data.users || []);
      } catch (err) {
        setMemberUsers([]);
      }
    };
    fetchMembers();
  }, [showCreateModal]);

  const handleBusinessCategoryChange = async (selectedOption) => {
    const categoryId = selectedOption ? selectedOption.value : '';
    setCreateFormValues(v => ({
      ...v,
      business_category: categoryId,
      business_sub_category: [],
    }));
    if (categoryId) {
      try {
        const response = await axios.get(`/admin/api/businessSubCategory?categoryId=${categoryId}`);
        setBusinessDropdowns(d => ({
          ...d,
          businessSubCategories: response.data?.businessSubCategories?.map(sc => ({
            value: sc._id,
            label: sc.name,
            category: sc.category,
          })) || [],
        }));
      } catch (err) {
        setBusinessDropdowns(d => ({ ...d, businessSubCategories: [] }));
      }
    } else {
      setBusinessDropdowns(d => ({ ...d, businessSubCategories: [] }));
    }
  };

  // Rename handleCreateUser to handleCreateFamily
  const handleCreateFamily = async () => {
    // Mobile number validation
    const mobile = createFormValues.mobileNumber;
    if (
      !mobile ||
      !/^\d{10}$/.test(mobile)
    ) {
      toastError('Mobile number must be exactly 10 digits and numeric only.');
      setCreateLoading(false);
      return;
    }
    setCreateLoading(true);

    try {
      const payload = {
        firstName: createFormValues.firstName,
        lastName: createFormValues.lastName,
        middleName: createFormValues.middleName,
        mobileNumber: createFormValues.mobileNumber,
        email: (createFormValues.email || '').toLowerCase(),
        joiningDate: createFormValues.joiningDate,
        userType: 'Family',
        business: [{
          name: createFormValues.business_name,
          category: createFormValues.business_category,
          subCategory: Array.isArray(createFormValues.business_sub_category)
            ? createFormValues.business_sub_category.join(',')
            : createFormValues.business_sub_category || '',
        }],
        plan: createFormValues.plan, // still send plan id
        fees: createFormValues.fees,
        source: createFormValues.source, // <-- Add this
        invitedByUser: createFormValues.invitedByUser, // <-- Add this
      };

      const res = await axios.post('/api/user/create', payload);

      if (res.data.Status === 1) {
        // ✅ Success
        toastSuccess(res.data.Message || "User created successfully!");
        setShowCreateModal(false);

        // Reset form
        setCreateFormValues({
          firstName: '',
          lastName: '',
          middleName: '',
          mobileNumber: '',
          email: '',
          business_name: '',
          business_category: '',
          business_sub_category: [],
          joiningDate: new Date().toISOString().split('T')[0],
          plan: '',
          planName: '', // add this
          fees: '',
          source: '',           // <-- Add this
          invitedByUser: '',    // <-- Add this
        });

        setCurrentPage(1);
        fetchUsers(1, searchQuery, filters);

      } else {
        // ❌ Failure cases
        if (res.data.Message === "User already exists") {
          toastError("A user with this email or mobile number already exists.");
        } else if (Array.isArray(res.data.errors) && res.data.errors.length > 0) {
          toastError(res.data.errors[0]);
        } else {
          toastError(res.data.Message || "Failed to create user");
        }
      }

    } catch (err) {
      const Message =
        err.response?.data?.Message ||
        err.response?.data?.error ||
        'Failed to create user';
      toastError(Message);
    }

    setCreateLoading(false);
  };
  // 🔒 Check permission
  if (!hasPermission(user, "Family", "Read")) {
    return (
      <div className="container py-4 text-center">
        <p className="text-danger fw-bold">
          🚫 You don’t have permission to view Familys.
        </p>
      </div>
    );
  }

  return (
    <div className="container mt-2">
      <h3 className="mb-0 text-dark mb-3">Family Member</h3>
      {/* Filter Row */}
      <div className="card mb-3">
        <div className="card-body py-2 d-flex flex-wrap align-items-end gap-3">
          <div>
            <label className="form-label mb-1 fw-bold">Date Filter:</label>
            <div className="form-check form-check-inline ms-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="joiningDate"
                value="joiningDate"
                checked={dateFilterType.includes('joiningDate')}
                onChange={e => {
                  const checked = e.target.checked;
                  setDateFilterType(checked ? ['joiningDate'] : []);
                  if (checked && dateRange.start && dateRange.end) {
                    fetchUsers(1, searchQuery);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="joiningDate">
                Joining Date
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="expirationDate"
                value="expirationDate"
                checked={dateFilterType.includes('expirationDate')}
                onChange={e => {
                  const checked = e.target.checked;
                  setDateFilterType(checked ? ['expirationDate'] : []);
                  if (checked && dateRange.start && dateRange.end) {
                    fetchUsers(1, searchQuery);
                  }
                }}
              />
              <label className="form-check-label" htmlFor="expirationDate">
                Expiration Date
              </label>
            </div>
          </div>
          <div>
            <label className="form-label mb-1 fw-bold">From</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.start}
              onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
              style={{ minWidth: 140 }}
            />
          </div>
          <div>
            <label className="form-label mb-1 fw-bold">To</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.end}
              onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
              style={{ minWidth: 140 }}
            />
          </div>
          <button
            className="btn btn-sm btn-primary"
            style={{ minWidth: 80 }}
            onClick={() => fetchUsers(1, searchQuery)}
            disabled={
              !dateRange.start ||
              !dateRange.end ||
              dateFilterType.length === 0
            }
          >
            Apply Filter
          </button>
          <button
            className="btn btn-sm btn-secondary"
            style={{ minWidth: 80 }}
            onClick={() => {
              setDateRange({ start: '', end: '' });
              setDateFilterType([]);
              fetchUsers(1, searchQuery);
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Add Family Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <form
              className="modal-content"
              onSubmit={e => {
                e.preventDefault();
                handleCreateFamily();
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title">Family Registration</h5>
                <button type="button" className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">First Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.firstName}
                      onChange={e => setCreateFormValues(v => ({ ...v, firstName: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Middle Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.middleName}
                      onChange={e => setCreateFormValues(v => ({ ...v, middleName: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Last Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.lastName}
                      onChange={e => setCreateFormValues(v => ({ ...v, lastName: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <hr className="my-2" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Mobile Number<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.mobileNumber}
                      onChange={e => setCreateFormValues(v => ({ ...v, mobileNumber: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Email ID<span className="text-danger">*</span></label>
                    <input
                      type="email"
                      className="form-control"
                      value={createFormValues.email}
                      onChange={e => setCreateFormValues(v => ({ ...v, email: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Joining Date<span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={createFormValues.joiningDate}
                      onChange={e => setCreateFormValues(v => ({ ...v, joiningDate: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <hr className="my-2" />
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Business Name<span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={createFormValues.business_name}
                      onChange={e => setCreateFormValues(v => ({ ...v, business_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      Business Category<span className="text-danger">*</span>
                    </label>
                    <Select
                      options={businessDropdowns.businessCategories}
                      placeholder="Select Category"
                      value={
                        businessDropdowns.businessCategories.find(
                          (opt) => opt.value === createFormValues.business_category
                        ) || null
                      }
                      onChange={handleBusinessCategoryChange}
                      classNamePrefix="select"
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      Business Sub Category<span className="text-danger">*</span>
                    </label>
                    <Select
                      options={businessDropdowns.businessSubCategories}
                      isMulti
                      placeholder="Select Sub Category"
                      value={businessDropdowns.businessSubCategories.filter(opt =>
                        (createFormValues.business_sub_category || []).includes(opt.value)
                      )}
                      onChange={selectedOptions => {
                        setCreateFormValues(v => ({
                          ...v,
                          business_sub_category: selectedOptions ? selectedOptions.map(opt => opt.value) : [],
                        }));
                      }}
                      classNamePrefix="select"
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">
                      Invite By <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={[
                        { value: 'socialMedia', label: 'Social Media' },
                        { value: 'user', label: 'User' }
                      ]}
                      placeholder="Select Invite By"
                      value={[
                        { value: 'socialMedia', label: 'Social Media' },
                        { value: 'user', label: 'User' }
                      ].find(opt => opt.value === createFormValues.source) || null}
                      onChange={selectedOption => {
                        setCreateFormValues(v => ({
                          ...v,
                          source: selectedOption ? selectedOption.value : '',
                          invitedByUser: '' // reset if changed
                        }));
                      }}
                      classNamePrefix="select"
                      required
                    />
                  </div>
                  {createFormValues.source === 'user' && (
                    <div className="col-md-4">
                      <label className="form-label">
                        Invited User <span className="text-danger">*</span>
                      </label>
                      <Select
                        options={memberUsers.map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))}
                        placeholder="Select User"
                        value={
                          memberUsers
                            .map(u => ({ value: u._id, label: `${u.firstName} ${u.lastName}` }))
                            .find(opt => opt.value === createFormValues.invitedByUser) || null
                        }
                        onChange={selectedOption => {
                          setCreateFormValues(v => ({
                            ...v,
                            invitedByUser: selectedOption ? selectedOption.value : ''
                          }));
                        }}
                        classNamePrefix="select"
                        required
                      />
                    </div>
                  )}
                  <div className="col-md-6">
                                    <label className="form-label">
                                      Plan <span className="text-danger">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={createFormValues.planName || ''}
                                      readOnly
                                      placeholder="No plan available"
                                      required
                                    />
                                    </div> 
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={createLoading}>
                  {createLoading ? "Save..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {importModalVisible && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.3)' }} tabIndex="-1">
          <div className="modal-dialog">
            <form className="modal-content" onSubmit={handleImportSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">Import Familys</h5>
                <button type="button" className="btn-close" onClick={handleCloseImportModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Select File</label>
                  <input type="file" accept=".csv,.xlsx" className="form-control" ref={importInputRef} onChange={handleImportFileChange} />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleDownloadSample}
                >
                  Download Sample File
                </button>
                <button type="submit" className="btn btn-success">Import</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0 text-dark">All Family</h5>
           {hasPermission(user, "Family", "Create") && (
            <button
              className="btn btn-sm btn-success text-white"
              onClick={() => setShowCreateModal(true)}
              type="button"
            >
              + Add Family
            </button>
          )}
        </div>
        <div
          className="table-responsive"
          style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
          ref={tableContainerRef} // Attach ref here
        >
          <div className="card-body p-0">
            <table
              className="table table-bordered table-sm mb-0"
              style={{ minWidth: 900 }} // Add this for guaranteed horizontal scroll
            >
              <thead className="table-light">
                <tr>
                  <th className="py-1">Sr no.</th>
                  <th className="py-1">First Name</th>
                  <th className="py-1">Middle Name</th>
                  <th className="py-1">Last Name</th>
                  <th className="py-1">Mobile Number</th>
                  <th className="py-1">Email</th>
                  <th className="py-1">Business Name</th>
                  <th className="py-1">Business Category</th>
                  <th className="py-1">Business Subcategory</th>
                  <th className="py-1">Joining Date</th>
                  <th className="py-1 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? (
                  users.map((u, idx) => (
                    <tr key={u._id}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>
                        <Link
                          to={`/family/view/${u._id}`}
                          style={{ color: '#C44C23', textDecoration: 'underline', cursor: 'pointer' }}
                        >
                          {u.firstName || '-'}
                        </Link>
                      </td>
                      <td>{u.middleName || '-'}</td>
                      <td>{u.lastName || '-'}</td>
                      <td>{u.mobileNumber || '-'}</td>
                      <td>{u.email || '-'}</td>
                      <td>{u.business && u.business.length > 0 ? u.business[0].name || '-' : '-'}</td>
                      <td>{u.business && u.business.length > 0 ? (Array.isArray(u.business[0].category) ? u.business[0].category.map(cat => cat.name || cat).join(', ') : (u.business[0].category?.name || u.business[0].category || '-')) : '-'}</td>
                      <td>{u.business && u.business.length > 0 ? (Array.isArray(u.business[0].subCategory) ? u.business[0].subCategory.map(sc => sc.name || sc).join(', ') : (u.business[0].subCategory?.name || u.business[0].subCategory || '-')) : '-'}</td>
                      <td>
                        {u.joiningDate
                          ? new Date(u.joiningDate).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="text-center">
                        {hasPermission(user, "Family", "Edit") && (
                          <Link
                            to={`/family/edit/${u._id}`}
                            className="btn btn-sm py-0 px-2 fs-9 lh-sm shadow-none border-0 text-primary-custom me-1"
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </Link>
                        )}
                        {hasPermission(user, "Family", "Delete") && (
                          <button
                            className="btn btn-sm py-0 px-2 fs-9 lh-sm shadow-none border-0 text-danger"
                            onClick={() => handleDelete(u._id)}
                            title="Delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="text-center">No Familys found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination & Limit */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2 pt-2">
          <div>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={limit}
              onChange={e => {
                setLimit(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {[10, 25, 50, 100].map(val => (
                <option key={val} value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div className="flex-grow-1"></div>
          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {(() => {
                const pages = [];
                const maxVisiblePages = 3;

                // Always show first page
                pages.push(
                  <button
                    key={1}
                    className={`btn btn-sm page-btn ${currentPage === 1 ? 'active' : ''}`}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </button>
                );

                // Show ellipsis if needed before current page range
                if (currentPage > maxVisiblePages + 1) {
                  pages.push(
                    <span key="ellipsis1" className="px-2">
                    ...
                    </span>
                  );
                }

                // Show pages around current page
                const startPage = Math.max(2, currentPage - 1);
                const endPage = Math.min(totalPages - 1, currentPage + 1);

                for (let page = startPage; page <= endPage; page++) {
                  if (page !== 1 && page !== totalPages) {
                    pages.push(
                      <button
                        key={page}
                        className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }
                }

                // Show ellipsis if needed after current page range
                if (currentPage < totalPages - maxVisiblePages) {
                  pages.push(
                    <span key="ellipsis2" className="px-2">
                    ...
                    </span>
                  );
                }

                // Always show last page if there's more than one page
                if (totalPages > 1) {
                  pages.push(
                    <button
                      key={totalPages}
                      className={`btn btn-sm page-btn ${currentPage === totalPages ? 'active' : ''}`}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pages;
              })()}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default FamilyIndex;